// The captain row (mockup round 091 card 05 as the owner amended it,
// R1889; ui-map-interface.md §12 "CAPTAIN").
//
// One row for every captain of CAPTAIN · WARDROOM and RECRUITMENT, and the
// row a captain picker builds on. It keeps the column idea of the fleet
// row (R1816) with three lines, so it is taller (`[presentation.caprow]`):
//
// - the portrait at `portrait_px`, over the three lines;
// - line 1: the full name alone, in the hero ink; the name opens the
//   captain card (R1118);
// - line 2: the state word in the cap step (AT SEA, DOCKED, ASHORE,
//   DIVING, OUTFITTING, TRAINING, CAPTURED, RECOVERING, CADET), then the
//   age and the phase, `age 41 · peak`; a cadet's line 2 is CADET alone;
// - line 3: the place or the job, `aboard Gull Wake`, `Helmsmanship ·
//   3h 10m`, dim and cut with an ellipsis; a cadet's line 3 puts the age
//   in front of the career line, `age 30 · rise · peak in 4 seasons`;
// - a cadet row puts the full name alone on line 1 and the ability strip,
//   RECRUIT and ⋯ level with CADET on line 2 (`cr-cad`);
// - the ability strip: six fixed cells under one column head
//   (`captainHeadHtml`) that names GUN to PRO once. A talent is the hero
//   ink with an underline, a shape and not a colour; the attribute in
//   training carries its progress as an underline in the accent; a picker
//   lights the column that matters in the accent;
// - the one press of a picker or of a cadet, and ⋯ with the per-captain
//   presses (one TRAIN press per ability, then RELEASE; or PAY), which
//   opens a grid under the row as the fleet row does (R1901).
//
// The module is pure: it reads the state and writes markup, and it holds
// no field of the overlay.

import { Building, type Captain, CaptainPhase, type Job, JobKind } from '../../gen/proto/aquaphobia';
import { CAPTAIN_ROW } from '../../gen/presentation';
import { card as CS, overlay as S } from '../../gen/strings';
import { type GameState, dur, num } from '../../state';
import { buttonHtml, type Action, type CardEnv } from '../cards/card';
import { esc } from '../esc';
import { fillPct } from '../format';
import { termButton, termsHtml } from '../glossary';
import { portraitHtml } from '../portrait/portraits';
import { phaseFromKind, storedLooks } from '../portrait/seed';
import { withResIcons } from '../resicon';
import { attrTrainFigures, careerText } from './captaincard';
import { diveName, outfitJob } from './markup';
import { ATTR_SHORT } from './tables';
import { clamp01 } from '../../render/math';

/** What a row reads beside the captain. */
export interface CapRowCtx {
  state: GameState;
  /** The own fleets at sea, which name the fleet a captain commands. */
  fleets: readonly { id: number; name: string }[];
  env: CardEnv;
  /** The names line under the column head stands open (R2176). */
  codesOpen?: boolean;
}

/** The options of one row. Every one is optional. */
interface CapRowOpts {
  /** The attribute column (0 to 5) a picker lights in the accent. */
  lit?: number;
  /** The one press at the right edge. `null` keeps its column empty, so
   *  the strip stays in line with the rows that carry a press. */
  press?: Action | null;
  /** The presses of ⋯. Absent: the row has no ⋯ column. Empty: the
   *  column stays, with no press, so the strip stays in line. */
  more?: Action[];
  /** The ⋯ grid stands open under the row. */
  moreOpen?: boolean;
  /** Markup in place of line 2 or line 3: a picker states its own figure
   *  there (the risk of a dive, the hull a captain would board). */
  line2?: string;
  line3?: string;
  selected?: boolean;
  /** A press on the whole row: a captain picker selects the captain with
   *  it (R1893). The row is then one button, so the name is plain text
   *  and the row carries no press and no ⋯, because a button holds no
   *  second button. */
  pick?: { act: string; id: number; n?: number };
}

/** The training job of one captain, or undefined. */
export function trainingJob(s: GameState, c: Captain): Job | undefined {
  return s.queue.find((j) => j.kind === JobKind.JOB_KIND_TRAINING && j.target === c.id);
}

/** The catalog name of attribute `attr`, or its short name before the
 *  catalog lands. */
export function attrName(s: GameState, attr: number): string {
  return (s.catalog?.attrs ?? [])[attr] ?? ATTR_SHORT[attr] ?? '';
}

/** The training job of one captain, as the lead of the Now fact of the
 *  card and the title of the TRAIN press in training (R1490, R1901): the
 *  attribute and its time left, or "Queued" while it holds no slot. */
function trainJobText(s: GameState, c: Captain): string {
  const job = trainingJob(s, c);
  if (!job) return '';
  const attr = attrName(s, job.count);
  return job.active ? S.captrain_training({ attr, left: dur(s.jobEtaSec(job)) }) : S.captrain_queued({ attr });
}

/** The TRAIN press of one ability (R1901). */
interface TrainGate {
  /** The captain takes TRAIN presses at all: a cadet and a prisoner do
   *  not, because the server refuses them. */
  show: boolean;
  /** The press acts now. */
  can: boolean;
  /** This attribute is in training: the press takes the accent. */
  on: boolean;
  /** What the press does, or the ONE reason it cannot act. */
  title: string;
}

/**
 * The one gate of the TRAIN press of attribute `attr` (R1901), which the
 * pick of the captain card (R2087) and ⋯ of the captain row both read, so the two never
 * disagree. The gates are the server's (`enqueueTraining`, and
 * `TrainingTimeSec` for the decline), and the title names the first gate
 * that fails, in this order: no Academy, a training job of this captain
 * (the press of the attribute in training states the job and its time),
 * a wound, the decline, `attr_train_cap`.
 */
export function trainGate(s: GameState, c: Captain, attr: number): TrainGate {
  if (c.cadet || c.capturedBy) return { show: false, can: false, on: false, title: '' };
  const job = trainingJob(s, c);
  const on = job?.count === attr;
  const off = (title: string): TrainGate => ({ show: true, can: false, on, title });
  if (s.level(Building.BUILDING_ACADEMY) === 0) return off(S.captrain_off_academy);
  if (job) return off(on ? trainJobText(s, c) : S.captrain_off_busy({ attr: attrName(s, job.count) }));
  if (c.injuredSecs > 0) return off(S.captrain_off_recovering({ left: dur(c.injuredSecs) }));
  if (c.phaseKind === CaptainPhase.CAPTAIN_PHASE_DECLINE) return off(S.captrain_off_decline);
  if (attrTrainFigures(c, attr, s.rules.captain).atCap) return off(S.captrain_at_cap);
  return { show: true, can: true, on, title: S.captrain_press_title({ attr: attrName(s, attr) }) };
}

/**
 * The one TRAIN press of the captain card (round 100 card 01, R2087),
 * which opens the pick of the six attributes. A cadet and a prisoner get
 * no press (`show` false). A training job of the captain closes it, and
 * `on` marks that case: the job's clock stands on its row, so the card
 * draws no press. Otherwise the title names the first gate that stops the
 * press, in the order of the card: the decline, no Academy, a wound,
 * every attribute at `attr_train_cap`. The pick reads `trainGate` for each
 * attribute, so a chip and ⋯ of the captain row never disagree.
 */
export function trainPickGate(s: GameState, c: Captain): TrainGate {
  if (c.cadet || c.capturedBy) return { show: false, can: false, on: false, title: '' };
  const job = trainingJob(s, c);
  const off = (title: string): TrainGate => ({ show: true, can: false, on: !!job, title });
  if (job) return off(trainJobText(s, c));
  if (c.phaseKind === CaptainPhase.CAPTAIN_PHASE_DECLINE) return off(S.captrain_off_decline);
  if (s.level(Building.BUILDING_ACADEMY) === 0) return off(S.captrain_off_academy);
  if (c.injuredSecs > 0) return off(S.captrain_off_recovering({ left: dur(c.injuredSecs) }));
  const r = s.rules.captain;
  if (c.attrs.every((_, i) => attrTrainFigures(c, i, r).atCap)) return off(S.captrain_at_cap);
  return { show: true, can: true, on: false, title: S.captrain_pick_title };
}

/** The share of the training job done, 0 to 1 (`State.jobLive`). */
function trainingDone(s: GameState, job: Job): number {
  if (job.totalMs <= 0) return 0;
  return clamp01(1 - s.jobLive(job).remainingMs / job.totalMs);
}

/** The phase word of the career strip: rise, peak or decline. */
export function phaseWord(c: Captain): string {
  switch (c.phaseKind) {
    case CaptainPhase.CAPTAIN_PHASE_DECLINE: return CS.career_decline;
    case CaptainPhase.CAPTAIN_PHASE_PEAK: return CS.career_peak;
    default: return CS.career_rise;
  }
}

/** The state word of line 2 and the markup of line 3. */
interface CapPlace {
  word: string;
  /** Markup, already escaped. */
  place: string;
}

/**
 * Where a captain is and what the captain does: the state word and line 3.
 * The order is the order of `captainBusy` (R737, R1483): a prisoner, a
 * wound, a dive, a dive that waits in the queue, then a training job (the
 * place follows it, because a captain at sea trains too), a fleet at sea,
 * a hull in the pens, and ashore. A fleet at sea is the button that
 * selects it (R1483); a hull in the pens is its plain name.
 */
export function captainPlace(ctx: CapRowCtx, c: Captain): CapPlace {
  const s = ctx.state;
  if (c.cadet) {
    return { word: S.caprow_st_cadet, place: esc(S.caprow_age_career({ age: c.age, career: careerText(c) })) };
  }
  if (c.capturedBy) {
    return {
      word: S.caprow_st_captured,
      place: withResIcons(esc(S.caprow_captured({ who: c.capturedBy, ransom: num(c.ransom) }))),
    };
  }
  if (c.injuredSecs > 0) {
    return { word: S.caprow_st_recovering, place: esc(S.caprow_recovering({ left: dur(c.injuredSecs) })) };
  }
  if (c.expedition) {
    const run = s.empire?.expeditions.find((x) => x.captainId === c.id);
    const dive = run ? diveName(s, run.depth) : '';
    return {
      word: S.caprow_st_diving,
      place: run ? esc(S.caprow_diving({ dive, left: dur(run.remainingSec) })) : esc(S.captain_expedition),
    };
  }
  const prep = outfitJob(s, c.id);
  if (prep) return { word: S.caprow_st_outfitting, place: esc(S.caprow_outfitting({ dive: diveName(s, prep.count) })) };
  const fleet = fleetPlace(ctx, c);
  const job = trainingJob(s, c);
  if (job) {
    const attr = attrName(s, job.count);
    const what = esc(job.active
      ? S.caprow_training({ attr, left: dur(s.jobEtaSec(job)) })
      : S.caprow_training_queued({ attr }));
    return { word: S.caprow_st_training, place: fleet ? `${what} · ${fleet.place}` : what };
  }
  return fleet ?? { word: S.caprow_st_ashore, place: esc(S.caprow_free) };
}

/** The name of a captain's fleet at sea as the press that selects the
 *  fleet and centres the map on it (R1483). */
export function capFleetButton(f: { id: number; name: string }): string {
  return `<button class="name info" data-act="capfleet" data-id="${f.id}"`
    + ` title="${esc(S.captain_fleet_title)}">${esc(f.name)}</button>`;
}

/** The fleet a captain commands, as a place: at sea or in the pens. */
function fleetPlace(ctx: CapRowCtx, c: Captain): CapPlace | null {
  if (!c.fleetId) return null;
  const sea = ctx.fleets.find((f) => f.id === c.fleetId);
  if (sea) return { word: S.caprow_st_at_sea, place: S.caprow_aboard({ name: capFleetButton(sea) }) };
  const pen = ctx.state.pennedHull(c.fleetId);
  if (pen) return { word: S.caprow_st_docked, place: esc(S.caprow_aboard({ name: pen.name })) };
  return { word: S.caprow_st_at_sea, place: esc(S.captain_commanding({ id: c.fleetId })) };
}

/**
 * The ability strip: six fixed cells, the value alone in each. `t` marks a
 * talent (the hero ink and an underline), `tr` the attribute in training
 * with its progress as an accent underline, `hi` the lit column.
 */
function abilityStripHtml(s: GameState, c: Captain, lit = -1): string {
  const job = c.cadet ? undefined : trainingJob(s, c);
  const cells = c.attrs.map((v, i) => {
    const tal = c.talents.includes(i);
    const name = attrName(s, i);
    const cls = [tal ? 't' : '', job?.count === i ? 'tr' : '', lit === i ? 'hi' : ''].filter(Boolean).join(' ');
    const title = tal ? S.caprow_cell_talent({ attr: name, v }) : S.caprow_cell_title({ attr: name, v });
    const prog = job?.count === i ? `<i style="width:${fillPct(trainingDone(s, job))}"></i>` : '';
    return `<span${cls ? ` class="${cls}"` : ''} role="img" title="${esc(title)}" aria-label="${esc(title)}">`
      + `<b>${v}</b>${prog}</span>`;
  });
  return `<span class="cr-ab">${cells.join('')}</span>`;
}

/** The right-hand columns of a row or of the head. */
function rightCols(press: string | undefined, more: string | undefined): string {
  return (press !== undefined ? `<span class="cr-press fl-press">${press}</span>` : '')
    + (more ?? '');
}

/**
 * The column head over a list of captain rows: the six short names once,
 * over the strip, and the empty columns of the press and of ⋯ that the
 * rows of the list carry. `lit` lights the column a picker reads. `names`
 * are the full names of the catalog (`Hello.catalog.attrs`): each short
 * name carries its full name as its title, so GUN, HLM, QUI, ENG, CMD and
 * PRO read whole on a hover.
 */
export function captainHeadHtml(
  opts: { lit?: number; press?: boolean; more?: boolean; names?: readonly string[]; open?: boolean } = {},
): string {
  // The strip of codes is ONE press (R2176): a press, a long press or a
  // keyboard focus shows the names line under the head, which pairs each
  // code with its full name, so no name hides behind a hover. The press
  // is the whole strip, so a finger finds it.
  const names = ATTR_SHORT.map((a, i) => opts.names?.[i] ?? a);
  const attrs = ATTR_SHORT.map((a, i) => `<span${opts.lit === i ? ' class="hi"' : ''}`
    + ` title="${esc(names[i])}"><em>${esc(a)}</em></span>`);
  const label = S.caprow_codes_label({ names: names.join(', ') });
  const key = ATTR_SHORT.map((a, i) => `<span><b>${esc(a)}</b> ${esc(names[i])}</span>`).join('');
  return '<div class="cr-row cr-head"><div class="cr-pt"></div><div class="cr-tx"></div>'
    + `<button type="button" class="cr-ab cr-codes" data-act="capcodes" aria-expanded="${!!opts.open}"`
    + ` aria-label="${esc(label)}">${attrs.join('')}</button>`
    + rightCols(opts.press ? '' : undefined, opts.more ? '<span class="cr-more fl-more"></span>' : undefined)
    + '</div>'
    + `<div class="cr-names${opts.open ? ' on' : ''}">${key}</div>`
    // One line says what a line under a figure means (R2176).
    + `<div class="cr-legend">${termsHtml(S.caprow_legend)}</div>`;
}

/**
 * One captain row, and the ⋯ grid under it while it is open (round 091
 * card 05, R1889). The shared builder of CAPTAIN · WARDROOM, RECRUITMENT
 * and every captain picker.
 */
export function captainRowHtml(ctx: CapRowCtx, c: Captain, opts: CapRowOpts = {}): string {
  const s = ctx.state;
  const cls = ['cr-row'];
  if (opts.selected) cls.push('sel');
  if (c.cadet) cls.push('cr-cad');
  const more = opts.more;
  const open = !!opts.moreOpen && (more?.length ?? 0) > 0;
  if (open) cls.push('open');
  // The portrait wears the empire's banner, because a captain of this
  // roster serves it; a face the Captain Designer froze rides the wire
  // (R1180, R1240).
  const face = portraitHtml({
    name: c.name, talents: c.talents, phase: phaseFromKind(c.phaseKind, c.age), faction: s.faction,
    ...storedLooks(c.portraitSeed, c.portraitGender),
  }, CAPTAIN_ROW.portraitPx, 'face');
  const place = opts.line2 === undefined || opts.line3 === undefined ? captainPlace(ctx, c) : null;
  // The phase is a term of the glossary (R2176), so "rise" reads as the
  // career phase; a picker row is itself a press and takes the plain word.
  const pick = opts.pick;
  const phase = pick ? esc(phaseWord(c)) : termButton('career_phase', phaseWord(c));
  // A cadet's line 3 carries the age and the career line, so line 2 holds
  // the state word alone (owner, 2026-11-02).
  const line2 = opts.line2 ?? `<span class="ord">${esc(place!.word)}</span>`
    + (c.cadet ? '' : ' · ' + S.caprow_phase_age({ phase, age: c.age }));
  const line3 = opts.line3 ?? place!.place;
  const name = pick ? `<span class="cr-nm">${esc(c.name)}</span>`
    : `<button class="info name" data-act="info" data-kind="captain" data-id="${c.id}"`
      + ` title="${esc(S.info_btn_captain_title)}">${esc(c.name)}</button>`;
  const press = pick || opts.press === undefined ? undefined : opts.press ? buttonHtml(opts.press, ctx.env) : '';
  const moreBtn = pick || more === undefined ? undefined : more.length > 0
    ? `<button class="cr-more fl-more${open ? ' on' : ''}" data-act="capmore" data-id="${c.id}"`
      + ` title="${esc(S.caprow_more_title({ name: c.name }))}" aria-expanded="${open}">${CS.more_mark}</button>`
    : '<span class="cr-more fl-more"></span>';
  const grid = open ? `<div class="fl-grid cr-grid">${more!.map((a) => buttonHtml(a, ctx.env)).join('')}</div>` : '';
  const inner = `<span class="cr-pt">${face}</span>`
    + `<span class="cr-tx"><span class="cr-l1">${name}</span>`
    + `<span class="cr-l2">${line2}</span><span class="cr-l3">${line3}</span></span>`
    + abilityStripHtml(s, c, opts.lit ?? -1)
    + rightCols(press, moreBtn);
  if (pick) {
    cls.push('pick');
    return `<button class="${cls.join(' ')}" data-act="${pick.act}" data-id="${pick.id}"`
      + (pick.n !== undefined ? ` data-n="${pick.n}"` : '')
      + ` aria-pressed="${!!opts.selected}">${inner}</button>`;
  }
  return `<div class="${cls.join(' ')}">${inner}</div>${grid}`;
}

/** The TRAIN press of each ability, as ⋯ of a captain row carries them
 *  (R1901): `TRAIN GUN` to `TRAIN PRO`, the full name or the one reason
 *  in the title, from `trainGate`. Empty for a cadet and a prisoner. */
function trainActions(s: GameState, c: Captain): Action[] {
  const out: Action[] = [];
  for (let i = 0; i < c.attrs.length; i++) {
    const g = trainGate(s, c, i);
    if (!g.show) return [];
    out.push({
      act: 'train', id: c.id, n: i, face: 'dark', on: g.on, off: !g.can, title: g.title,
      label: S.captrain_row({ short: ATTR_SHORT[i] ?? '' }),
    });
  }
  return out;
}

/**
 * The presses one captain takes, in the order of ⋯ and of the card's
 * action row (R1889, R1901): RECRUIT and PASS of a cadet, PAY of a
 * prisoner, and for a roster captain one TRAIN press per ability, then
 * RELEASE while the captain commands no fleet, because the server refuses
 * that release (R696). A captain at sea trains, so a commander keeps the
 * TRAIN presses. RELEASE is armed; PASS keeps one press (R696 rule 3).
 * `armed` reads the overlay's arm gate. `train: false` leaves the TRAIN
 * presses out: the captain card carries its own TRAIN press and pick
 * under the ability rows (R2087).
 */
export function captainActions(
  s: GameState, c: Captain, armed: (key: string) => boolean, opts: { train?: boolean } = {},
): Action[] {
  const price = s.empire?.passPrice ?? 0;
  const canPay = s.res.credits >= price;
  const priced = (label: string): Pick<Action, 'label' | 'labelHtml'> => ({ label, labelHtml: withResIcons(esc(label)) });
  if (c.cadet) {
    return [
      { act: 'recruit', id: c.id, face: 'dark', label: S.btn_recruit },
      { act: 'dismiss', id: c.id, face: 'quiet', off: !canPay, ...priced(S.btn_pass({ price: num(price) })) },
    ];
  }
  if (c.capturedBy) {
    return [{ act: 'ransom', id: c.id, face: 'dark', off: s.res.credits < c.ransom, ...priced(S.btn_pay({ ransom: c.ransom })) }];
  }
  const train = opts.train === false ? [] : trainActions(s, c);
  if (c.fleetId !== 0) return train;
  const on = armed(`dismiss:${c.id}`);
  return [...train, {
    act: 'dismiss', id: c.id, face: 'quiet', arm: true, off: !canPay, armed: on,
    ...priced(S.btn_release_price({ price: num(price) })), armLabel: S.release_armed,
  }];
}
